import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';

// Константы
const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'];

// Тип для тарифных данных
interface TariffData {
  name: string;
  value: number;
  price: number;
  period: string;
}

// Словарь для отображения кодов тарифов
const TARIFF_DISPLAY_NAMES: Record<string, string> = {
  'weekly': 'Неделя',
  'biweekly': '2 недели',
  'monthly': 'Месяц',
  'rent-to-own': 'С выкупом',
  'unknown': 'Неизвестный тариф'
};

const RentalDurationAnalytics: React.FC = () => {
  const [tariffData, setTariffData] = useState<TariffData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDemoData, setUsingDemoData] = useState<boolean>(false);
  
  useEffect(() => {
    const fetchTariffData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Запрашиваем все аренды
        const { data: rentalsData, error: rentalsError } = await supabase
          .from('rentals')
          .select('*');
          
        if (rentalsError) {
          console.error('Ошибка при получении данных аренды:', rentalsError);
          throw new Error('Не удалось загрузить данные аренды');
        }

        // Группировка данных по tariff_type
        const groupedRentals = rentalsData.reduce((acc: Record<string, number>, item: any) => {
          const tariff = item.tariff_type || 'unknown';
          acc[tariff] = (acc[tariff] || 0) + 1;
          return acc;
        }, {});
        
        console.log('Сгруппированные данные по тарифам:', groupedRentals);
        
        // Получаем информацию о тарифах
        const { data: tariffsData, error: tariffsError } = await supabase
          .from('tariffs')
          .select('*');
          
        if (tariffsError) {
          console.error('Ошибка при получении данных о тарифах:', tariffsError);
          throw new Error('Не удалось загрузить данные о тарифах');
        }
        
        // Проверяем, есть ли хотя бы один тариф с ненулевым значением
        const hasRealData = Object.values(groupedRentals).some((count: any) => Number(count) > 0);
        
        if (!hasRealData) {
          console.log('Нет данных о тарифах аренд');
          setTariffData([]);
          setUsingDemoData(false);
          return;
        }
        
        // Используем только тарифы, которые есть в базе данных Supabase
        // и отфильтровываем дублирующиеся значения
        const mappedData: TariffData[] = tariffsData
          .filter(tariff => ['weekly', 'biweekly', 'monthly', 'rent-to-own', 'unknown'].includes(tariff.name))
          .map(tariff => {
            const tariffName = tariff.name;
            return {
              name: TARIFF_DISPLAY_NAMES[tariffName] || tariffName,
              value: groupedRentals[tariffName] || 0,
              price: parseFloat(tariff.price as any) || 0,
              period: tariff.period || 'Н/Д'
            };
          });
        
        setTariffData(mappedData);
        setUsingDemoData(false);
      } catch (error) {
        console.error('Ошибка при загрузке данных о тарифах:', error);
        setError('Не удалось загрузить данные.');
        setTariffData([]);
        setUsingDemoData(false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTariffData();
  }, []);

  // Рассчитываем общее количество аренд
  const totalRentals = tariffData.reduce((sum, item) => sum + item.value, 0);
  
  // Рассчитываем процентное соотношение для каждого тарифа
  const getPercentage = (value: number) => {
    return totalRentals > 0 ? Math.round((value / totalRentals) * 100) : 0;
  };

  // Функция для форматирования числа как валюты
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', { 
      style: 'currency', 
      currency: 'RUB', 
      maximumFractionDigits: 0 
    }).format(amount);
  };

  if (loading) {
    return (
      <Card className="bg-card/50">
        <CardHeader>
          <CardTitle>Распределение по тарифам аренды</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Загрузка данных...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <Card className="bg-card/50">
        <CardHeader>
          <CardTitle>
            Распределение по тарифам аренды
            {usingDemoData && (
              <span className="text-sm ml-2 text-amber-500 font-normal">
                (демо-данные)
              </span>
            )}
          </CardTitle>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="h-[300px]">
            {totalRentals > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tariffData.filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {tariffData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => [`${value} аренд (${getPercentage(value)}%)`, "Количество"]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground">
                  <p>Нет данных о распределении тарифов</p>
                  <p className="text-sm mt-2">Создайте аренды с тарифами для отображения статистики</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="space-y-4">
            {tariffData.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant="outline" 
                      className={`
                        ${index === 0 ? 'bg-purple-100 text-purple-800 border-purple-300' : 
                          index === 1 ? 'bg-blue-100 text-blue-800 border-blue-300' : 
                          index === 2 ? 'bg-green-100 text-green-800 border-green-300' : 
                          'bg-amber-100 text-amber-800 border-amber-300'}
                      `}
                    >
                      {item.name}
                    </Badge>
                    <span className="text-sm">{getPercentage(item.value)}%</span>
                  </div>
                  <div className="text-sm font-medium">{item.value} аренд</div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <div>Стоимость: {formatCurrency(item.price)}</div>
                  <div>{item.period}</div>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`
                      ${index === 0 ? 'bg-purple-600' : 
                        index === 1 ? 'bg-blue-600' : 
                        index === 2 ? 'bg-green-600' : 
                        'bg-amber-600'} 
                      h-2 rounded-full
                    `}
                    style={{ width: `${getPercentage(item.value)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card/50">
        <CardHeader>
          <CardTitle>
            Ключевые показатели по тарифам
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">Всего аренд</div>
              <div className="text-2xl font-bold">{totalRentals}</div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">Самый популярный тариф</div>
              <div className="text-2xl font-bold">
                {tariffData.length > 0 && totalRentals > 0 ? 
                  tariffData.reduce((max, item) => max.value > item.value ? max : item, tariffData[0]).name : 
                  'Н/Д'}
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">Средний чек</div>
              <div className="text-2xl font-bold">
                {formatCurrency(
                  totalRentals > 0 ? 
                  tariffData.reduce((sum, item) => sum + (item.price * item.value), 0) / totalRentals : 
                  0
                )}
              </div>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">Общий доход</div>
              <div className="text-2xl font-bold">
                {formatCurrency(
                  tariffData.reduce((sum, item) => sum + (item.price * item.value), 0)
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RentalDurationAnalytics;
